// Maps video time -> snapshot frame ("latest frame at or before t"), swaps
// the snapshot <img>, preloads neighbors, and renders the timeline markers.
import { $, el } from './dom';
import { fmtMs } from './format';
import * as playerMod from './player';
import { state } from './state';

const preloaded = new Map<number, HTMLImageElement>();

/**
 * Index of the last frame whose (calibrated) video position starts at or
 * before t, else 0. video_ms is the middle of the snapshot's video frame, so
 * the boundary is half a frame earlier.
 */
export function timeToFrameIndex(seconds: number): number {
  const frames = state.manifest.frames;
  const halfFrameMs = 500 / state.manifest.fps;
  const ms = seconds * 1000 + 15; // epsilon: landing exactly on the boundary selects it
  let lo = 0;
  let hi = frames.length - 1;
  let ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].video_ms - halfFrameMs <= ms) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

export function setFrameByTime(seconds: number): void {
  const idx = timeToFrameIndex(seconds);
  if (idx !== state.frameIndex) {
    state.frameIndex = idx;
    applyFrame();
    state.emit('frame');
  }
}

/** Jump the snapshot pane and the video to a given manifest frame. */
export function gotoFrameIndex(idx: number): void {
  const frames = state.manifest.frames;
  const clamped = Math.min(Math.max(idx, 0), frames.length - 1);
  // video_ms is mid-frame already — immune to boundary rounding on seek
  playerMod.seekTo(frames[clamped].video_ms / 1000);
}

export function gotoFrameMs(ms: number): void {
  const idx = state.manifest.frames.findIndex((f) => f.ms === ms);
  if (idx >= 0) gotoFrameIndex(idx);
}

export function stepSnapshot(dir: 1 | -1): void {
  // Derive the position from the video's own time, not the pane state: while
  // a seek is still in flight, currentTime already reports the pending target
  // (per spec), so rapid clicks each advance one snapshot instead of
  // re-reading a stale index and swallowing every second click.
  gotoFrameIndex(timeToFrameIndex(playerMod.currentTime()) + dir);
}

function applyFrame(): void {
  const frame = state.currentFrame;
  if (!frame) return;
  $<HTMLImageElement>('#frameImg').src = frame.src;
  $('#frameTime').textContent = fmtMs(frame.ms);
  $('#frameIndex').textContent = `snapshot ${state.frameIndex + 1}/${state.manifest.frames.length}`;
  updateCurrentTick();
  preloadAround(state.frameIndex);
}

function preloadAround(idx: number): void {
  for (const offset of [1, -1, 2, -2]) {
    const frame = state.manifest.frames[idx + offset];
    if (!frame || preloaded.has(frame.ms)) continue;
    const img = new Image();
    img.src = frame.src;
    preloaded.set(frame.ms, img);
    if (preloaded.size > 40) {
      const first = preloaded.keys().next().value;
      if (first !== undefined) preloaded.delete(first);
    }
  }
}

// ------------------------------------------------------------ marker bar

export function initMarkers(): void {
  renderMarkers();
  state.addEventListener('annotated', renderMarkers);
  state.addEventListener('frame', updateCurrentTick);
  playerMod.onDurationKnown(renderMarkers);

  $<HTMLElement>('#markerBar').addEventListener('click', (e) => {
    const bar = $('#markerBar');
    const rect = bar.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    playerMod.seekTo(ratio * playerMod.duration(), false);
  });
}

function renderMarkers(): void {
  const bar = $('#markerBar');
  bar.innerHTML = '';
  const total = playerMod.duration() * 1000;
  if (!total) return;
  for (let i = 0; i < state.manifest.frames.length; i++) {
    const frame = state.manifest.frames[i];
    const tick = el('div', 'tick');
    tick.style.left = `${(frame.ms / total) * 100}%`;
    const count = state.annotated.get(frame.ms);
    if (count) {
      tick.classList.add('annotated');
      tick.title = `${fmtMs(frame.ms)} — ${count} polygon${count > 1 ? 's' : ''}`;
    } else {
      tick.title = fmtMs(frame.ms);
    }
    tick.dataset.ms = String(frame.ms);
    const idx = i;
    tick.addEventListener('click', (e) => {
      e.stopPropagation();
      gotoFrameIndex(idx);
    });
    bar.appendChild(tick);
  }
  updateCurrentTick();
}

function updateCurrentTick(): void {
  const bar = $('#markerBar');
  bar.querySelector('.tick.current')?.classList.remove('current');
  const ms = state.currentFrame?.ms;
  if (ms !== undefined) {
    bar.querySelector(`.tick[data-ms="${ms}"]`)?.classList.add('current');
  }
}
